import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { KieneSession } from './kiene-session';
import { SearchFilterValue } from './search-filter-value';
import { Client } from '../../components/client/classes/client';
import { Mandant } from '../../components/mandanten/classes/mandant';
import { KieneUtils } from '../../utils/KieneUtils';

@Injectable({ providedIn: 'root' })
export class LocalStorageService {
    /** Seed / portal Benutzerrolle „Sammelstelle“. */
    static readonly BENUTZERROLLE_SAMMELSTELLE_ID = 31;

    private currentKieneSession: KieneSession = null;
    private swappedKieneSession: KieneSession = null;
    private showShoppingCart = false;
    private readonly showShoppingCartSubject = new Subject<boolean>();
    private readonly changeCurrentMandantSubject = new Subject<Mandant>();
    private readonly numberOfShoppingCartItemsSubject = new Subject<number>();
    private readonly currentUserSubject = new Subject<KieneSession>();
    private readonly currentClientSubject = new Subject<Client>();
    private readonly loggedInSubject = new Subject<boolean>();
    private readonly showCurrentClientSubject = new Subject<boolean>();

    constructor() {}

    watchShowShoppingCart(): Observable<boolean> {
        return this.showShoppingCartSubject.asObservable();
    }

    watchChangeCurrentMandant(): Observable<Mandant> {
        return this.changeCurrentMandantSubject.asObservable();
    }

    setShoppingCartVisible(visible: boolean, sender: string) {
        this.showShoppingCart = visible;
        console.log('setShoppingCartVisible: ' + visible + '(' + sender + ')');
        this.showShoppingCartSubject.next(visible);
    }

    getShowShoppingCart(): boolean {
        return this.showShoppingCart;
    }

    setNumberOfShoppingCartItems(items: number) {
        this.numberOfShoppingCartItemsSubject.next(items);
    }

    watchNumberOfShoppingCartItems(): Observable<number> {
        return this.numberOfShoppingCartItemsSubject.asObservable();
    }

    watchCurrentUser(): Observable<KieneSession> {
        return this.currentUserSubject.asObservable();
    }

    setCurrentClient(client: Client, sender: string) {
        localStorage.setItem('kieneCurrentClient', client ? JSON.stringify(client) : null);
        console.log(sender, client);
        this.currentClientSubject.next(client);
    }

    watchCurrentClient(): Observable<Client> {
        return this.currentClientSubject.asObservable();
    }

    getCurrentClient(): Client {
        const str = localStorage.getItem('kieneCurrentClient');
        if (str && str !== 'undefined') {
            return JSON.parse(str);
        } else {
            return null;
        }
    }

    /** API returns schwein/rind as strings; avoid truthy checks on "0". */
    isCurrentClientSchwein(): boolean {
        return Number(this.getCurrentClient()?.schwein) > 0;
    }

    isCurrentClientRind(): boolean {
        return Number(this.getCurrentClient()?.rind) > 0;
    }

    isCurrentClientFreigabe(): boolean {
        return Number(this.getCurrentClient()?.freigabe) > 0;
    }

    isCurrentClientModulAllowed(modul: 'belege' | 'tierbewegung' | 'bio'): boolean {
        if (!this.isCurrentClientFreigabe()) {
            return true;
        }
        const client = this.getCurrentClient();
        const raw: unknown =
            modul === 'belege'
                ? client?.modul_belege
                : modul === 'tierbewegung'
                  ? client?.modul_tierbewegung
                  : client?.modul_bio;
        if (raw == null || raw === '') {
            return true;
        }
        return Number(raw) > 0;
    }

    isRecordModulAllowed(
        record: { darf_bio?: unknown } | null | undefined,
        modul: 'belege' | 'tierbewegung' | 'bio',
    ): boolean {
        if (modul === 'bio' && record && record.darf_bio != null && record.darf_bio !== '') {
            return Number(record.darf_bio) > 0;
        }
        return this.isCurrentClientModulAllowed(modul);
    }

    getModulBlockedFallbackUrl(): string {
        if (this.isCurrentUserKunde() || this.isCurrentUserViehhaendler()) {
            return '/profil';
        }
        if (this.getCurrentUser()?.menue?.startseite) {
            return '/startseite';
        }
        return '/profil';
    }

    private isMenuBlockedForFreigabeClient(path: string): boolean {
        if (!this.isCurrentClientFreigabe()) {
            return false;
        }
        const menu = (path || '').split('?')[0];
        if (
            menu === '/belege' ||
            menu.startsWith('/belege/') ||
            menu === '/labor' ||
            menu.startsWith('/labor/') ||
            menu === '/dokumente' ||
            menu.startsWith('/dokumente/')
        ) {
            return !this.isCurrentClientModulAllowed('belege');
        }
        if (menu === '/gruppen' || menu.startsWith('/gruppen/') || menu === '/meldungen' || menu.startsWith('/meldungen/')) {
            return !this.isCurrentClientModulAllowed('tierbewegung');
        }
        return false;
    }

    static isClientSchwein(client: Client | null | undefined): boolean {
        return Number(client?.schwein) > 0;
    }

    static isClientRind(client: Client | null | undefined): boolean {
        return Number(client?.rind) > 0;
    }

    setCurrentMandant(mandant: Mandant) {
        localStorage.setItem('currentMandant', JSON.stringify(mandant));
        this.changeCurrentMandantSubject.next(mandant);
    }

    getCurrentMandant(): Mandant {
        return <Mandant>JSON.parse(localStorage.getItem('currentMandant'));
    }

    removeKieneSession() {
        localStorage.removeItem('kieneSession');
        this.currentKieneSession = null;
    }

    setCurrentUser(kieneSession: KieneSession) {
        this.removeKieneSession();
        localStorage.setItem('kieneSession', JSON.stringify(kieneSession));
        this.currentKieneSession = kieneSession;
        this.currentUserSubject.next(kieneSession);
    }

    setSwappedUser(SwappedKieneSession: KieneSession) {
        localStorage.setItem('swappedKieneSession', JSON.stringify(SwappedKieneSession));
        this.swappedKieneSession = SwappedKieneSession;
    }

    getSwappedUser(): KieneSession {
        if (this.swappedKieneSession) {
            return this.swappedKieneSession;
        }
        this.swappedKieneSession = JSON.parse(localStorage.getItem('swappedKieneSession'));
        return this.swappedKieneSession;
    }

    deleteSwappedUserEntity() {
        localStorage.removeItem('swappedKieneSession');
        this.swappedKieneSession = undefined;
    }

    isLoggedIn(): boolean {
        return !!this.getCurrentUser();
    }

    watchLoggedIn(): Observable<boolean> {
        return this.loggedInSubject.asObservable();
    }

    setLoggedIn(loggedIn: boolean) {
        this.loggedInSubject.next(loggedIn);
    }

    getCurrentUser(): KieneSession {
        if (this.currentKieneSession) {
            return this.currentKieneSession;
        }
        const raw = localStorage.getItem('kieneSession');
        if (!raw) {
            return null;
        }
        try {
            this.currentKieneSession = JSON.parse(raw);
        } catch {
            localStorage.removeItem('kieneSession');
            this.currentKieneSession = null;
        }

        return this.currentKieneSession;
    }

    currentUserCanSeeMenu(menu: string): boolean {
        const kieneSession = this.getCurrentUser();

        if (kieneSession == null) {
            return false;
        }

        if (this.isMenuBlockedForFreigabeClient(menu)) {
            return false;
        }
        if (Number(kieneSession.admin) > 0) {
            return true;
        }
        if (kieneSession.menue == null) {
            return false;
        }
        const splittedMenu: string[] = menu.split('/');

        return kieneSession.menue[splittedMenu[1]] != null;
    }

    public hasPermission(permissionId: number): boolean {
        return this.currentUserHasPermission(permissionId);
    }

    /**
     * Explizites Recht in der Session-Liste (ohne Admin-Bypass).
     * Für Soft-Trennung Labor/Praxis: Mandant-Admin bekommt 1082 nicht automatisch.
     */
    public hasExplicitPermission(permissionId: number): boolean {
        const kieneSession = this.getCurrentUser();
        if (kieneSession == null) {
            return false;
        }
        return this.sessionHasPermission(kieneSession, permissionId);
    }

    currentUserIsAdmin(): boolean {
        const kieneSession = this.getCurrentUser();

        if (kieneSession == null) {
            return false;
        }

        return kieneSession.admin > 0;
    }

    private isSessionAdmin(kieneSession: KieneSession): boolean {
        return Number(kieneSession.admin) > 0;
    }

    private sessionHasPermission(kieneSession: KieneSession, permissionId: number): boolean {
        return !!kieneSession.benutzerrecht_ids?.some(id => Number(id) === Number(permissionId));
    }

    currentUserHasPermission(permissionId: number): boolean {
        const kieneSession = this.getCurrentUser();

        if (kieneSession == null) {
            return false;
        }

        if (this.isSessionAdmin(kieneSession)) {
            return true;
        }

        return this.sessionHasPermission(kieneSession, permissionId);
    }

    /** Sonderrechte (ohne_admin): nur echte Zuweisung, kein Admin-Kurzschluss. */
    currentUserHasExplicitPermission(permissionId: number): boolean {
        const kieneSession = this.getCurrentUser();
        if (kieneSession == null) {
            return false;
        }
        return this.sessionHasPermission(kieneSession, permissionId);
    }

    currentUserHasPermissions(ids: number[]): boolean {
        if (!ids || ids.length === 0) {
            return false;
        }

        // Template/JSON liefert IDs oft als String – Number() statt typeof === 'number'
        const normalized = ids.map(id => Number(id)).filter(id => !Number.isNaN(id) && id > 0);
        if (normalized.length === 0) {
            return false;
        }

        const kieneSession = this.getCurrentUser();
        if (!kieneSession) {
            return false;
        }

        if (this.isSessionAdmin(kieneSession)) {
            return true;
        }

        for (const id of normalized) {
            if (this.sessionHasPermission(kieneSession, id)) {
                return true;
            }
        }

        return false;
    }

    isCurrentUserPhitoUser(): boolean {
        const kieneSession = this.getCurrentUser();
        if (kieneSession == null) {
            return false;
        }
        return kieneSession.email?.endsWith('@phito.de');
    }

    /** Matches betrieb/kunde read_one_pin allowlist (admin + these ids) for HIT-PIN UI. */
    private static readonly HIT_PIN_USER_IDS = [3, 751, 895];

    /** Hardcoded allowlist for Reporte Overview Dashboard. */
    private static readonly REPORTE_DASHBOARD_USER_IDS = [1, 751, 895];

    isCurrentUserVetvetSupport(): boolean {
        const u = this.getCurrentUser();
        if (u?.benutzer_id != null && LocalStorageService.HIT_PIN_USER_IDS.includes(Number(u.benutzer_id))) {
            return true;
        }
        return u?.email === 'malte.rehe@vetvet.de' || this.isCurrentUserPhitoUser();
    }

    canAccessReporteDashboard(): boolean {
        const u = this.getCurrentUser();
        return u?.benutzer_id != null && LocalStorageService.REPORTE_DASHBOARD_USER_IDS.includes(Number(u.benutzer_id));
    }

    getSearchFilterValue(componentName: string): string {
        const searchFilterValues: SearchFilterValue[] = JSON.parse(localStorage.getItem('searchFilterValues'));

        if (searchFilterValues == null) {
            return '';
        }
        for (const v of searchFilterValues) {
            if (v.componentName === componentName) {
                return v.filterValue;
            }
        }

        return '';
    }

    setSearchFilterValue(componentName: string, filterValue: string) {
        const sfv = new SearchFilterValue(componentName, filterValue);

        let searchFilterValues: SearchFilterValue[] = JSON.parse(localStorage.getItem('searchFilterValues'));
        if (searchFilterValues == null) {
            searchFilterValues = [];
        }

        let found = false;
        for (const v of searchFilterValues) {
            if (v.componentName === componentName) {
                v.filterValue = filterValue;
                found = true;
            }
        }

        if (!found) {
            searchFilterValues.push(sfv);
        }

        localStorage.setItem('searchFilterValues', JSON.stringify(searchFilterValues));
    }

    setDebugToken(token: string) {
        if (this.getCurrentUser().admin) {
            this.removeDebugToken();
            localStorage.setItem('kieneDebugToken', token);
        }
    }

    getDebugToken() {
        if (this.getCurrentUser().admin) {
            return localStorage.getItem('kieneDebugToken');
        }
        return '';
    }

    removeDebugToken() {
        localStorage.removeItem('kieneDebugToken');
    }

    deleteBrowserSession(sender: string) {
        console.log('deleteBrowserSession: ' + sender);
        this.removeKieneSession();
        localStorage.removeItem('kieneCurrentClient');
        localStorage.removeItem('currentMandant');
        localStorage.removeItem('searchFilterValues');
        this.removeDebugToken();
        this.deleteSwappedUserEntity();
    }

    isCurrentUserViehhaendler(): boolean {
        return this.isUserViehhaendler(this.getCurrentUser());
    }

    isUserViehhaendler(u: KieneSession): boolean {
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.viehhaendler > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserViehhaendlerAdmin(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.viehhaendler > 0 && rolle?.admin_der_rolle > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserSupport(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.support > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserSpediteur(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.spediteur > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserFahrer(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle.bezeichnung === 'Fahrer') {
                return true;
            }
        }
        return false;
    }

    isCurrentUserSchlachthof(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.schlachthof > 0) {
                return true;
            }
        }
        return false;
    }

    isUserKunde(u: KieneSession): boolean {
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.kunde > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserKunde(): boolean {
        return this.isUserKunde(this.getCurrentUser());
    }

    isCurrentUserKundeAdmin(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.kunde > 0 && rolle?.admin_der_rolle > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserSgs(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (rolle?.sgs > 0) {
                return true;
            }
        }
        return false;
    }

    isCurrentUserVerwaltung(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        if (u.benutzerrollen) {
            for (const rolle of u.benutzerrollen) {
                if (rolle?.verwaltung > 0) {
                    return true;
                }
            }
        }
        return false;
    }

    /** Benutzerrolle „Sammelstelle“ (seed id 31). */
    isCurrentUserSammelstelle(): boolean {
        const u = this.getCurrentUser();
        if (!u || !u.benutzerrollen) {
            return false;
        }
        for (const rolle of u.benutzerrollen) {
            if (Number(rolle?.benutzerrolle_id) === LocalStorageService.BENUTZERROLLE_SAMMELSTELLE_ID) {
                return true;
            }
        }
        return false;
    }

    /** Sammelstelle UI: Benutzerrolle Sammelstelle (31) oder Admin. */
    canUseSammelstelle(): boolean {
        if (this.currentUserIsAdmin()) {
            return true;
        }
        return this.isCurrentUserSammelstelle();
    }

    watchShowCurrentClient(): Observable<boolean> {
        return this.showCurrentClientSubject.asObservable();
    }

    setCurrentClientVisible(visible: boolean) {
        this.showCurrentClientSubject.next(visible);
    }

    currentUserIsVet(): boolean {
        return this.getCurrentUser().tierarzt_id && this.getCurrentUser().tierarzt_id >= 0;
    }

    currentUserCanSeeTab(menuTab: string): boolean {
        const kieneSession = this.getCurrentUser();

        if (kieneSession == null) {
            return false;
        }
        if (this.isMenuBlockedForFreigabeClient(menuTab)) {
            return false;
        }
        if (Number(kieneSession.admin) > 0) {
            return true;
        }

        const splittedMenu: string[] = menuTab.split('/');
        const tabName = splittedMenu[2];

        if (kieneSession?.menue && kieneSession?.menue[splittedMenu[1]]) {
            const tabList: string[] = kieneSession?.menue[splittedMenu[1]];
            return tabList && tabList.includes(tabName);
        }
        return false;
    }

    public hasAllPermissions(permissionIds: number[]): boolean {
        for (const p of permissionIds) {
            if (!this.hasPermission(p)) {
                return false;
            }
        }
        return true;
    }

    public getStartseite(): string {
        if (this.isCurrentUserSchlachthof()) {
            return '/schlachthoefe';
        }
        if (this.isCurrentUserFahrer()) {
            return '/spediteur/fahrer-offene-touren';
        }
        if (this.isCurrentUserSpediteur()) {
            return '/spediteur';
        }
        // Kunde
        if (this.isCurrentUserKunde()) {
            return '/gruppen/dashboard';
        }
        if (this.isCurrentUserSgs()) {
            return '/gruppen';
        }
        if (this.isCurrentUserViehhaendler()) {
            return '/gruppen/dashboard';
        }
        if (this.getCurrentUser().menue?.startseite) {
            return '/startseite';
        }
        if (this.getCurrentUser().menue?.belege) {
            return '/belege';
        }
        if (this.getCurrentUser().menue?.labor) {
            return '/labor';
        }
        if (this.getCurrentUser().menue?.lager) {
            return '/lager';
        }
        return '';
    }

    isCurrentUserVeterinarian() {
        return KieneUtils.isNNoU(this.getCurrentUser().tierarzt_id);
    }
}
